// =====================================================================================
// ReconnectionStrategy - Manages reconnection logic with exponential backoff
// =====================================================================================
// Consolidates retry mechanisms into a single, clean pattern
// Implements exponential backoff and configurable retry limits

import type { IReconnectionStrategy, VideoError } from './types.js';

export class ReconnectionStrategy implements IReconnectionStrategy {
  private readonly maxAttempts: number;
  private readonly baseDelay: number;
  private readonly maxDelay: number;
  private readonly backoffMultiplier: number;
  private scheduledRetry: NodeJS.Timeout | null = null;

  // Error codes that should not trigger retry
  private readonly noRetryErrors = new Set([
    'CLIENT_INITIATED',      // User disconnected intentionally
    'DUPLICATE_IDENTITY',    // Another client with same identity connected
    'CONNECTION_FAILED',     // Initial connection failed (not a disconnect)
    'TOKEN_TIMEOUT',        // Token request timed out
    'CONNECTION_TIMEOUT'    // Connection attempt timed out
  ]);

  constructor(
    maxAttempts = 3,
    baseDelay = 2000,
    maxDelay = 30000,
    backoffMultiplier = 1.5
  ) {
    this.maxAttempts = maxAttempts;
    this.baseDelay = baseDelay;
    this.maxDelay = maxDelay;
    this.backoffMultiplier = backoffMultiplier;

    console.info('🎥 ReconnectionStrategy initialized:', {
      maxAttempts,
      baseDelay,
      maxDelay,
      backoffMultiplier
    });
  }

  shouldRetry(attempts: number, error?: VideoError): boolean {
    // Check if max attempts exceeded
    if (attempts >= this.maxAttempts) {
      console.info('🎥 Max reconnection attempts reached:', attempts);
      return false;
    }

    // Check if error code is in no-retry list
    if (error && this.noRetryErrors.has(error.code)) {
      console.info('🎥 Error code indicates no retry:', error.code);
      return false;
    }

    // Special case: always retry for stale room errors
    if (error?.message?.includes('leave-reconnect')) {
      console.info('🎥 Stale room detected, forcing retry');
      return true;
    }

    // Check for network-related errors that should be retried
    if (error) {
      const networkErrors = ['UNEXPECTED_DISCONNECT', 'NETWORK_ERROR', 'WEBSOCKET_ERROR'];
      if (networkErrors.includes(error.code)) {
        console.info('🎥 Network error detected, will retry:', error.code);
        return true;
      }
    }

    // Default: retry if attempts are under limit
    return true;
  }

  getRetryDelay(attempts: number): number {
    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.baseDelay * Math.pow(this.backoffMultiplier, attempts),
      this.maxDelay
    );

    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 0.3 * delay; // Up to 30% jitter
    const finalDelay = Math.floor(delay + jitter);

    console.info(`🎥 Calculated retry delay: ${finalDelay}ms for attempt ${attempts + 1}`);
    return finalDelay;
  }

  scheduleRetry(callback: () => void, attempts: number): void {
    // Cancel any existing scheduled retry
    this.cancelScheduledRetry();

    const delay = this.getRetryDelay(attempts);
    console.info(`🎥 Scheduling retry in ${delay / 1000} seconds (attempt ${attempts + 1}/${this.maxAttempts})`);

    this.scheduledRetry = setTimeout(() => {
      this.scheduledRetry = null;
      callback();
    }, delay);
  }

  cancelScheduledRetry(): void {
    if (this.scheduledRetry) {
      console.info('🎥 Cancelling scheduled retry');
      clearTimeout(this.scheduledRetry);
      this.scheduledRetry = null;
    }
  }

  reset(): void {
    this.cancelScheduledRetry();
  }

  // Utility method to parse disconnect reasons from LiveKit
  static parseDisconnectReason(reason: any): {
    isUserInitiated: boolean;
    isDuplicateIdentity: boolean;
    isConnectionFailure: boolean;
    shouldRetry: boolean;
    message: string;
  } {
    const reasonStr = String(reason);

    // LiveKit can send numeric or string disconnect reasons
    const isUserInitiated = reasonStr === 'CLIENT_INITIATED' || reasonStr === '1';
    const isDuplicateIdentity = reasonStr === 'DUPLICATE_IDENTITY' || reasonStr === '2';
    const isConnectionFailure = reasonStr === '0'; // Connection failed before establishing

    // Determine if we should retry based on the reason
    const shouldRetry = !isUserInitiated && !isDuplicateIdentity && !isConnectionFailure;

    // Generate human-readable message
    let message = 'Connection lost';
    if (isUserInitiated) {
      message = 'Disconnected by user';
    } else if (isDuplicateIdentity) {
      message = 'Another client connected with same identity';
    } else if (isConnectionFailure) {
      message = 'Failed to establish connection';
    } else if (reasonStr) {
      message = `Connection lost: ${reasonStr}`;
    }

    return {
      isUserInitiated,
      isDuplicateIdentity,
      isConnectionFailure,
      shouldRetry,
      message
    };
  }

  // Get current configuration for testing/debugging
  getConfig() {
    return {
      maxAttempts: this.maxAttempts,
      baseDelay: this.baseDelay,
      maxDelay: this.maxDelay,
      backoffMultiplier: this.backoffMultiplier
    };
  }
}